import "./SportsCard.css";

import type { SportRecord } from "../../api/sports";

const MEDIA_API_URL = "https://api.ayesearch.com/api/crawl/media-gallery/";

interface MediaImage {
  thumbnail_url?: string;
  source_url?: string;
  alt?: string;
  caption?: string;
  focal_x?: number;
  focal_y?: number;
}

interface MediaResponse {
  images?: MediaImage[];
  source_url?: string;
}

function escapeHtml(value: unknown): string {
  return String(value ?? "")
    .replace(/&/g, "&amp;")
    .replace(/</g, "&lt;")
    .replace(/>/g, "&gt;")
    .replace(/"/g, "&quot;")
    .replace(/'/g, "&#039;");
}

function renderDetail(label: string, value: string): string {
  if (!value.trim()) return "";

  return `
    <div class="sports-card-detail">
      <span>${escapeHtml(label)}</span>
      <strong>${escapeHtml(value)}</strong>
    </div>
  `;
}

function focalPercent(value: unknown): number {
  const n = Number(value);
  return Number.isFinite(n) ? Math.max(0, Math.min(100, n * 100)) : 50;
}

async function hydrateTableTennisMedia(): Promise<void> {
  const host = document.querySelector<HTMLElement>(
    '[data-sports-card="table-tennis"] [data-sports-media]',
  );
  if (!host) return;

  try {
    const url = new URL(MEDIA_API_URL);
    url.searchParams.set("slug", "table-tennis");
    url.searchParams.set("limit", "3");

    const response = await fetch(url.toString(), {
      method: "GET",
      credentials: "include",
      headers: { Accept: "application/json" },
    });
    if (!response.ok) return;

    const data = (await response.json()) as MediaResponse;
    const images = Array.isArray(data.images)
      ? data.images.filter((item) => item?.thumbnail_url).slice(0, 3)
      : [];
    if (!images.length) return;

    host.innerHTML = `
      <div class="sports-card-media-grid sports-card-media-grid--${images.length}">
        ${images.map((image) => {
          const href = image.source_url || data.source_url || "";
          const label = image.caption || image.alt || "Table tennis";
          const picture = `
            <img
              src="${escapeHtml(image.thumbnail_url)}"
              alt="${escapeHtml(label)}"
              loading="lazy"
              style="object-position:${focalPercent(image.focal_x)}% ${focalPercent(image.focal_y)}%"
            />`;
          return href
            ? `<a class="sports-card-media-item" href="${escapeHtml(href)}" target="_blank" rel="noopener noreferrer" title="${escapeHtml(label)}">${picture}</a>`
            : `<div class="sports-card-media-item">${picture}</div>`;
        }).join("")}
      </div>
      <div class="sports-card-media-credit">Images sourced from Wikimedia/Wikipedia crawl cache</div>
    `;
    host.hidden = false;
  } catch {
    // Optional visual enrichment: never suppress the structured sports card.
  }
}

export function renderSportsCard(sport: SportRecord): string {
  const categoryLine = [sport.category, sport.subcategory]
    .filter(Boolean)
    .join(" · ");

  const equipment = Array.isArray(sport.equipment)
    ? sport.equipment.filter(Boolean)
    : [];

  const leagues = Array.isArray(sport.leagues)
    ? sport.leagues.filter((item) => item && item.name)
    : [];

  const governingBody = String(sport.governing_body || "").trim();
  const format = String(sport.team_size || "").trim();
  const hasMoreDetails = Boolean(
    governingBody || format || equipment.length || leagues.length,
  );

  if (sport.slug === "table-tennis") {
    queueMicrotask(() => void hydrateTableTennisMedia());
  }

  return `
    <article class="sports-card" data-sports-card="${escapeHtml(sport.slug)}">
      <div class="sports-card-topline">
        <span class="sports-card-kicker">AYESEARCH SPORTS</span>
        ${
          sport.is_verified
            ? '<span class="sports-card-verified">Verified</span>'
            : ""
        }
      </div>

      <div class="sports-card-heading">
        <div>
          <h1>${escapeHtml(sport.common_name)}</h1>
          ${
            categoryLine
              ? `<p class="sports-card-category">${escapeHtml(categoryLine)}</p>`
              : ""
          }
        </div>

        ${
          sport.sport_type
            ? `<span class="sports-card-type">${escapeHtml(sport.sport_type)}</span>`
            : ""
        }
      </div>

      ${
        sport.summary
          ? `<p class="sports-card-summary">${escapeHtml(sport.summary)}</p>`
          : ""
      }

      ${sport.slug === "table-tennis" ? '<section class="sports-card-media" data-sports-media hidden></section>' : ""}

      ${
        hasMoreDetails
          ? `
            <details class="sports-card-more">
              <summary class="sports-card-more-toggle">
                <span class="sports-card-more-label sports-card-more-label--closed">Show more</span>
                <span class="sports-card-more-label sports-card-more-label--open">Show less</span>
                <span class="sports-card-more-chevron" aria-hidden="true"></span>
              </summary>

              <div class="sports-card-more-content">
                ${
                  governingBody || format
                    ? `
                      <div class="sports-card-details">
                        ${renderDetail("Governing body", governingBody)}
                        ${renderDetail("Format", format)}
                      </div>
                    `
                    : ""
                }

                ${
                  equipment.length
                    ? `
                      <section class="sports-card-equipment">
                        <h2>Equipment</h2>
                        <div class="sports-card-chips">
                          ${equipment
                            .map(
                              (item) =>
                                `<span class="sports-card-chip">${escapeHtml(item)}</span>`,
                            )
                            .join("")}
                        </div>
                      </section>
                    `
                    : ""
                }

                ${
                  leagues.length
                    ? `
                      <section class="sports-card-equipment">
                        <h2>Competitions</h2>
                        <div class="sports-card-chips">
                          ${leagues
                            .map(
                              (item) =>
                                `<span class="sports-card-chip">${escapeHtml(item.short_name || item.name)}</span>`,
                            )
                            .join("")}
                        </div>
                      </section>
                    `
                    : ""
                }
              </div>
            </details>
          `
          : ""
      }
    </article>
  `;
}
